import { Notification } from "./Notification";
import { Validator } from "./Validator";
import { v4 as uuidv4 } from "uuid";
import * as machinemotion from "../api/machinemotion";
import environment from "../environment";
import widgets from "../legacy/widgets";
import { ConfigManager } from "./ConfigManager";
import { ApplicationState, reservedUuids } from "./constants";
import { Application } from "./Application";
import { _uniqueName, Seq } from "./utils";
import * as ApplicationFactory from './factories/ApplicationFactory';
import { ApplicationTypes } from "./constants/ApplicationTypes";
import { ApplicationBase } from "./ApplicationBase";
import { action, makeObservable, observable, computed } from "mobx";
import { setMachineLogicConfiguration } from './MachineLogicConfiguration';

/**
 * Constructor for the Library object. pConfigInfo is provided by the
 * machinemotion.getConfigInfo API call.
 *
 * @param {Object} pConfigInfo Contains versioning and device information
 *                 about the MachineLogic engine.
 * @param {import('./MachineLogicConfiguration').MachineLogicConfiguration} pMachineLogicConfiguration Static configuration for MachineLogic
 *
 * @extends {Notification} Notification
 */
export function Library(pConfigInfo, pMachineLogicConfiguration) {
  Notification.call(this);
  setMachineLogicConfiguration(pMachineLogicConfiguration);

  if(!pConfigInfo) {
    // The config needs information, we could fetch it from the exec engine at initialization, but in the CAD, Library.initialize and Config.initialized are overwritten.
    // Fetching it here, because it's asynchronous, is far from ideal. Instead it should be passed as an argument.
    console.warn('Library expects config information argument. Setting it to a default value. This might not be what you expect.');
    pConfigInfo = {
      libraryVersion  : 4,
      hostControllerUuid : undefined
    };
  }

  console.log('Initializing Library with Configuration: ', pConfigInfo);

  this.mLibraryVersion  = pConfigInfo.libraryVersion;
  this.mConfigManager   = new ConfigManager(this, pConfigInfo);
  this.mInstructionSet  = {};
  this.mApplications    = [];
  /** The non-saved application list behaves like the application list, EXCEPT they are never saved the Library. */
  this.mNonSavedApplications = [];
  this.mParameters      = [];
  this.mCalibrationParts = [];
  this.mCurrentApplication = null;
  this.mValidator = new Validator(this);
  this.mIsInitialized = false;
  this.mMachineLogicSubscriptionStatus; // This is a CAD only attribute, used to track the machine_logic subscription status of the library.

  makeObservable(this, {
    mApplications: observable,
    mCurrentApplication: observable,
    mIsInitialized: observable,
    mValidator: observable,
    mConfigManager: observable,
    currentApplication: computed,
    machineLogicAppList: computed,
    validator: computed,
    configManager: computed,
    selectApplication: action,
    newApplication: action,
    addApplication: action,
    copyApplication: action,
    removeApplication: action,
    reset: action,
    initialize: action,
  });
}

Library.prototype = Object.create(Notification.prototype);
Library.prototype.constructor = Library;

/**
 * Retrieve the version with which all applications should be saved.
 * @returns {Number} Current library version.
 */
Library.prototype.getLibraryVersion = function() {
  return this.mLibraryVersion;
};

/**
 * Always pass true to the pCompletion function
 * @param {function} pCompletion take true of false as parameter.
 */
Library.prototype.isVersionDownloadable = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  return true;
};

Library.prototype.getName = function() {
  return "Library";
};

Library.prototype.isInitialized = function() {
  return this.mIsInitialized;
};

/**
 * Retrieves the configuration manager.
 *
 * @returns {ConfigManager} The Configuration Manager.
 */
Library.prototype.getConfigManager = function() {
  return this.mConfigManager;
};

Object.defineProperty(
  Library.prototype,
  'configManager',
  {
    get: function () {
      return this.getConfigManager();
    }
  }
);

/**
 * Retrieves the validator for this library.
 *
 * @return {Validator}
 */
Library.prototype.getValidator = function() {
  return this.mValidator;
};

Object.defineProperty(
  Library.prototype,
  'validator',
  {
    get: function () {
      return this.getValidator();
    }
  }
);

Library.prototype.getInstructionSet = function() {
  return this.mInstructionSet;
};

Library.prototype.getMachineLogicSubscriptionStatus = function() {
  return this.mMachineLogicSubscriptionStatus;
};

Library.prototype._getNotDeprecatedActionSet = function (pCategory){
  const pFullInstructionSet = this.getInstructionSet()[pCategory];
  const filteredInstructionSet = {};
  Object.keys(pFullInstructionSet).forEach(function(key){
    if ( !(pFullInstructionSet[key].deprecated && pFullInstructionSet[key].deprecated == true) ){
      filteredInstructionSet[key] = pFullInstructionSet[key];
    }
  });
  return filteredInstructionSet;
};

/**
 * This function filter the instructions
 * compatible with a specific axis type
 * @param {String} pCategory - category (motor, define, trigger)
 * @param {Axis} pAxis - Axis class
 * @param {pUnit} pUnit, axis or axes, combined moves have a different structure (an array), we use this to specify if we want regular move or combined ...
 */
Library.prototype._getAxisCompatibleActionSet = function(pCategory, pAxis, pUnit = 'axis') {
  const lNotDeprecatedInstructions = this._getNotDeprecatedActionSet(pCategory);
  if (!pAxis) { return lNotDeprecatedInstructions; }
  const filteredInstructions = Object.keys(lNotDeprecatedInstructions).reduce(function(acc, currentInstruction) {
    const lInstructionAllowed = lNotDeprecatedInstructions[currentInstruction].parameters.find(
      function(pParameter) {
        if(pUnit === 'axis') {
          return pParameter.allowedAxisType && pParameter.unit === 'axis' && pParameter.allowedAxisType.includes(pAxis.getType());
        } else if(pUnit === 'axes') {
          // NOTE: Get Combined instructions, the structure is parameters.unit[ {allowedAxisType:[], unit:'axis'} ]
          if(!pParameter.unit || !Array.isArray(pParameter.unit)) { return false; }
          const lFound = pParameter.unit.find(function(pUnitParameter) {
            return pUnitParameter.allowedAxisType && pUnitParameter.unit === 'axis' && pUnitParameter.allowedAxisType.includes(pAxis.getType());
          });
          return !!lFound;
        }
        else {
          console.error('getAxisCompatibleActionSet is expecting an `axes | axis` unit');
          return false;
        }
      }
    );

    if (lInstructionAllowed) { acc[currentInstruction] = lNotDeprecatedInstructions[currentInstruction]; }

    return acc;
  }, {});

  return filteredInstructions;
};

/**
 * This function return the instructions
 * options when the all axes is selected
 */
Library.prototype._getAllAxesActionSet = function() {
  const lInstructionSet = this.getInstructionSet();
  return Object.keys(lInstructionSet).reduce(function (acc, curr) {
    Object.keys(lInstructionSet[curr]).forEach(function (instruction) {
      if (lInstructionSet[curr][instruction].hasOwnProperty('uiProps') && lInstructionSet[curr][instruction].uiProps.filteredByAllAxes) {
        acc[instruction] = lInstructionSet[curr][instruction];
      }
    });
    return acc;
  }, {});
};

/**
 * Due to the all axes option we add a placeholder
 * option of type axis that filter specific instructions
 * related to the whole system
 */
Library.prototype._getAllAxesParameters = function() {
  return {
    default: reservedUuids.ALL,
    description: 'The axis to be moved',
    name: 'axis',
    unit: 'axis',
    format: 'oneOf',
    values: [0, 1, 2, 3, 4, 5, 6],
  };
};

/**
 * Returns the list of all applications, both saved and non-saved.
 * @returns {ApplicationBase[]}
 */
Library.prototype.getAllApplications = function() {
  return this.mApplications.concat(this.mNonSavedApplications);
};

/**
 * Returns only the list of applications that are NOT SAVED to the library.
 * @returns {ApplicationBase[]}
 */
Library.prototype.getNonSavedApplications = function() { return this.mNonSavedApplications; };

/**
 * Returns ONLY the list of standard MachineLogic applications.
 * @returns {Application[]}
 */
Library.prototype.getMachineLogicAppList = function() {
  return this.getAllApplications().filter(app => app.getType() === ApplicationTypes.MachineLogic);
};


Object.defineProperty(
  Library.prototype,
  'machineLogicAppList',
  {
    get: function () {
      return this.getMachineLogicAppList().concat(this.getMachineAppApplications());
    }
  }
);

/**
 * Returns ONLY the list of MachineApp applications.
 * @returns {import('./InjectableMachineAppApplication').InjectableMachineAppApplication[]}
 */
Library.prototype.getMachineAppApplications = function() {
  return this.getAllApplications().filter(app => app.getType() === ApplicationTypes.MachineApp);
};

/**
 * Returns ONLY the list of Python applications.
 * @returns {import('./PythonApplication').PythonApplication}
 */
Library.prototype.getPythonAppList = function() {
  return this.getAllApplications().filter(app => app.getType() === ApplicationTypes.Python);
};

/**
 * List all applications, both permanent and temporary, by name.
 * @returns {string[]}
 */
Library.prototype.listApplications = function() {
  return this.getAllApplications().map(function(pApp) { return pApp.getName(); });
};

Library.prototype.uniqueApplicationName = function(pAppName) {
  return _uniqueName(pAppName, this.listApplications());
};

/**
 * Finds and returns the application with the given name.
 *
 * @param {string} pApplicationName The name of the application to get.
 * @returns {Application} The application with the given name.
 */
Library.prototype.getApplication = function(pApplicationName) {
  return this.getAllApplications().find(pApplication => pApplication.getName() === pApplicationName);
};

Library.prototype.getApplicationById = function(pId) {
  return this.mApplications.find(function(pApp) { return pApp.mId == pId; });
};

Library.prototype.addApplication = function(pData) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lExistingApp = this.getApplication(pData.name);

  // An app of that name already exists
  if(_lExistingApp) {
    // Rename the incoming one
    const _lNewName = this.uniqueApplicationName(pData.name);
    console.warn('Application [' + pData.name + '] already exists, renaming to [' + _lNewName + ']');
    pData.name = _lNewName;
    pData.id = uuidv4();
  }

  const lApp = ApplicationFactory.fromJson(this, pData);
  this.mApplications.push(lApp);

  this.mConfigManager.addApplicationInCalibrationParts(lApp);

  this.notify('onApplicationAdded', lApp, this.mApplications.length - 1);
  return lApp;
};

Library.prototype.newApplication = function(pType = ApplicationTypes.MachineLogic) {
  // TODO: Unfortunately, we need to call 'addApplication' on every app that is going to 'mApplications' list
  // so that all of the correct notifications trigger. I have made a ticket to refactor this:
  // https://github.com/VentionCo/mm-programmatic-sdk/issues/22
  const _lNewApp = this.addApplication(ApplicationFactory.createJson(this, pType));
  _lNewApp.setDirty();
  return _lNewApp;
};

/**
 * Clones the provided application and adds it to the library.
 * @param {ApplicationBase} pApp
 * @returns {ApplicationBase} The new application
 */
Library.prototype.copyApplication = function(pApp) {
  // TODO: Unfortunately, we need to call 'addApplication' on every app that is going to 'mApplications' list
  // so that all of the correct notifications trigger. I have made a ticket to refactor this:
  // https://github.com/VentionCo/mm-programmatic-sdk/issues/22
  const lApp = this.addApplication(pApp.cloneToNewJson());
  lApp.setDirty();
  return lApp;
};

/**
 * Uploads the provided application JSON to the library.
 * We will first run a patch on the application, in case it is not yet up to date with the latest version.
 * Then, we will patch the application with appropriate axes identifiers, before saving it.
 *
 * @param {Library} pSourceData Library uploaded via the UI
 * @param {Function<Boolean, String>} pOnComplete Callback for when complete. First argument is success, second argument is reason.
 */
Library.prototype.uploadApplication = function(pSourceData, pOnComplete) {
  //
  // -- A quick note on nomenclature:
  //
  //      When you see the word 'source', that is referring to the application/config
  //      that has been uploaded via the file selector. This data is arriving to us as
  //      a raw JSON object.
  //
  //      When you see the word 'destination', that is referring to the instance of
  //      the Library that you currently have in memory.
  //

  const lThis         = this;
  const lInstructionSet = this.getInstructionSet();
  const lConfigManager  = this.getConfigManager();

  if (!pSourceData.applications || pSourceData.applications.length === 0) {
    pOnComplete(false, 'No applications were provided for uploading.');
    return;
  }

  if (pSourceData.applications.length === 1) {
    if (pSourceData.applications[0].name === '#global') {
      pOnComplete(false, 'Upload must contain at least one non-configuration application.');
      return;
    } else {
      pOnComplete(false, 'Unable to upload an application without a configuration.');
      return;
    }
  }

  machinemotion.patchLibrary(pSourceData, function(pDidPatch, pNewData) {
    // -- With the app patched, let's resolve the Unique IDs so that it works with the target configuration.
    if (pDidPatch) {
      pSourceData = pNewData;
    }

    const lSourceConfigApp = pSourceData.applications.find(function(pApp) { return pApp.name && pApp.name === '#global'; });

    if (!lSourceConfigApp) {
      console.error('Failing to upload the application because the config app does not exist.');
      pOnComplete(false, 'Configuration application no longer existed after patching.');
      return;
    }

    const lSourceConfigSeq = lSourceConfigApp.sequences.find(function(pSeq) { return pSeq.name === lSourceConfigApp.initialSequence; });
    if (!lSourceConfigSeq) {
      console.error('Failing to upload the application because the startup sequence does not exist.');
      pOnComplete(false, 'Configuration sequence no longer existed after patching.');
      return;
    }

    const lSourceControllers = lSourceConfigSeq.instructions.filter(function(pInst) { return pInst.name === 'controller'; });

    // UUID resolution methods
    const lTryResolveController = function(pControllerIp, pControllerIndex) {
      const lConfigList = lConfigManager.getConfigurationList();
      const lConfig = lConfigList.find(function(pConfig) { return pConfig.getController().getIpAddress() === pControllerIp; });
      if (lConfig) {
        return lConfig; // Successfully found it by IP address.
      }

      if (pControllerIndex >= 0 && pControllerIndex < lConfigList.length) {
        return lConfigList[pControllerIndex]; // Return a controller at the appropriate index
      }

      return undefined;
    };
    const lTryResolveAxis = function(pControllerIp, pControllerIndex, pColor) {
      // -- Attempts to find an axis in our current config that exists on the provided controller and has the particular color index.
      const lConfig = lTryResolveController(pControllerIp, pControllerIndex);
      if ( lConfig == undefined ) { return undefined; }
      return lConfig.listAxes().find(function(pAxis) { return pAxis.getColor() == pColor; });
    };
    const lTryResolvePneumatic = function(pControllerIp, pControllerIndex, pDevice, pPin) {
      // -- Find an pneumatic axis according to its device number and "push out" pin number combination.
      //    This combination gets persisted in the configuration payload, and should be unique to every pneumatic actuator of the config.
      const lConfig = lTryResolveController(pControllerIp, pControllerIndex);
      if ( lConfig == undefined ) { return undefined; }
      const lAxis = lConfig.listAxes().find(function(pAxis) {
        if (pAxis.isNonMotorized() && pAxis.getDevice() == pDevice) {
          return pAxis.getPin('pushOutPin') == pPin;
        }
        else {
          return false;
        }
      });

      return lAxis;
    };
    const lTryResolveOutput = function(pControllerIp, pControllerIndex, pDevice, pPin) {
      const lConfig = lTryResolveController(pControllerIp, pControllerIndex);
      if ( lConfig == undefined ) { return undefined; }
      return lConfig.listOutputs().find(function(pOutput) { return pOutput.getDevice() == pDevice && pOutput.getPin() == pPin; });
    };
    const lTryResolveInput = function(pControllerIp, pControllerIndex, pDevice, pPin) {
      const lConfig = lTryResolveController(pControllerIp, pControllerIndex);
      if ( lConfig == undefined ) { return undefined; }
      return lConfig.listInputs().find(function(pInput) { return pInput.getDevice() == pDevice && pInput.getPin() == pPin; });
    };
    const lTryResolveRobot = function(pControllerIp, pControllerIndex, pRobotType) {
      const lConfig = lTryResolveController(pControllerIp, pControllerIndex);
      if ( lConfig == undefined ) { return undefined; }
      return lConfig.listRobots().find(pRobot => pRobot.getType() == pRobotType);
    };
    const lResolveUnitForSingleParam = function(pInstruction, pParam) {
      if (!pParam || !pParam.unit) {
        return;
      }

      if (Array.isArray(pParam.unit)) {
        pInstruction.parameters[pParam.name].forEach(function(pArrayElement) {
          // Note that we wrap the array element as parameters so that the function can treat it
          // as though it were a standalone value.
          lResolveUnitForSingleParam({ parameters: pArrayElement }, pParam.unit[0]);
        });
      }
      else {
        switch (pParam.unit) {
          case 'axis': {
            if(pInstruction.category == 'waitFor' && pInstruction.name == 'motionCompletion') {
              // we cant path ALL actuators
              if(pInstruction.parameters.axis == reservedUuids.ALL) { break; }
            }
            // [Oliver Levy]: I don't love the way we check this.
            // We should come up with a new actuator type called io-dependent-actuator since we now offer the ac motor which is not a pneumatic actuator.
            if (pParam.allowedAxisType.length === 1 && ['pneumatic', 'ac_motor_with_vfd', 'pneumatic_bag_gripper'].includes(pParam.allowedAxisType[0])) {
              // -- Pneumatic reference resolution
              const lSourcePneumaticUuid = pInstruction.parameters[pParam.name];
              const lSourcePneumaticInfo = lSourceConfigSeq.instructions.find(function(pInst) { return pInst.name === 'pneumaticAxis' && pInst.parameters.uuid === lSourcePneumaticUuid; });
              const lDestinationResolvedPneumatic = lSourcePneumaticInfo ? lTryResolvePneumatic(
                lSourceControllers.find(controllerInst => controllerInst.parameters.uuid === lSourcePneumaticInfo.parameters.controllerId).parameters.ipAddress,
                lSourceControllers.findIndex(function(pController) { return pController.parameters.uuid === lSourcePneumaticInfo.parameters.controllerId; }),
                lSourcePneumaticInfo.parameters.device,
                lSourcePneumaticInfo.parameters.pushOutPin
              ) : undefined;

              pInstruction.parameters[pParam.name] = lDestinationResolvedPneumatic ? lDestinationResolvedPneumatic.getUUID() : reservedUuids.UNKNOWN;

            }
            else {
              // -- Axis reference resolution
              const lSourceAxisUuid = pInstruction.parameters[pParam.name];
              const lSourceAxisInfo = lSourceConfigSeq.instructions.find(function(pInst) { return pInst.name === 'axis' && pInst.parameters.uuid === lSourceAxisUuid; });
              if(lSourceAxisUuid === undefined){
                // patch for older apps with combined moves, if actuator was "Not Selected", "axis" property doesn't exist at all rather than just "axis" : ""
                // https://github.com/VentionCo/mm-machine-logic/issues/2554
                pInstruction.parameters[pParam.name] = reservedUuids.NONE;
              }
              else if (lSourceAxisInfo === undefined) {
                pInstruction.parameters[pParam.name] = lSourceAxisUuid === reservedUuids.NONE ? reservedUuids.NONE : reservedUuids.UNKNOWN;
              }
              else {
                const lDestinationResolvedAxis = lTryResolveAxis(
                  lSourceControllers.find(controllerInst => controllerInst.parameters.uuid === lSourceAxisInfo.parameters.controllerId).parameters.ipAddress,
                  lSourceControllers.findIndex(function(pController) { return pController.parameters.uuid === lSourceAxisInfo.parameters.controllerId; }),
                  lSourceAxisInfo.parameters.axisColor
                );
                pInstruction.parameters[pParam.name] = lDestinationResolvedAxis ?  lDestinationResolvedAxis.getUUID() : reservedUuids.UNKNOWN;
              }
            }

            break;
          }
          case 'ioExpander': {
            // -- Input/Output reference resolution
            const lSourceIoUuid = pInstruction.parameters[pParam.name];
            const lSourceIo = lSourceConfigSeq.instructions.find(function(pInst) { return pInst.name === 'alias' && pInst.parameters.uuid === lSourceIoUuid; });
            let lDestinationResolvedIo;

            if (lSourceIo && lSourceIo.parameters.category === 'output') {
              lDestinationResolvedIo = lTryResolveOutput(
                lSourceControllers.find(controllerInst => controllerInst.parameters.uuid === lSourceIo.parameters.controllerId).parameters.ipAddress,
                lSourceControllers.findIndex(function(pController) { return pController.parameters.uuid === lSourceIo.parameters.controllerId; }),
                lSourceIo.parameters.parameters.device,
                lSourceIo.parameters.parameters.pin
              );
            }
            else if (lSourceIo && lSourceIo.parameters.category === 'input') {
              lDestinationResolvedIo = lTryResolveInput(
                lSourceControllers.find(controllerInst => controllerInst.parameters.uuid === lSourceIo.parameters.controllerId).parameters.ipAddress,
                lSourceControllers.findIndex(function(pController) { return pController.parameters.uuid === lSourceIo.parameters.controllerId; }),
                lSourceIo.parameters.parameters.device,
                lSourceIo.parameters.parameters.pin
              );
            }

            pInstruction.parameters[pParam.name] = lDestinationResolvedIo ? lDestinationResolvedIo.getUUID() : reservedUuids.UNKNOWN;
            break;
          }
          case 'string': {
            if ( pParam.name === 'robotId' ) {
              const lSourceRobotId = pInstruction.parameters.robotId;
              const lSourceRobot = lSourceConfigSeq.instructions.find(function(pInst) { return pInst.name === 'robot' && pInst.parameters.robotId === lSourceRobotId; });
              const lDestinationResolvedRobot = lSourceRobot
                ? lTryResolveRobot(
                  lSourceControllers.find((controllerInst) => controllerInst.parameters.uuid === lSourceRobot.parameters.controllerId).parameters.ipAddress,
                  lSourceControllers.findIndex((pController) => pController.parameters.uuid === lSourceRobot.parameters.controllerId),
                  lSourceRobot.parameters.robotType
                )
                : undefined;
              pInstruction.parameters.robotId = lDestinationResolvedRobot ? lDestinationResolvedRobot.getRobotId() : reservedUuids.UNKNOWN;
            }
            break;
          }
        }
      }
    };

    // patch all applications
    const pUploadedApps = pSourceData.applications.map((pApp) => {
      if (pApp.name && pApp.name == '#global') { return null; }

      // When we upload and download the apps, we clear the deployedByMachineCloud flag
      pApp.deployedByMachineCloud = false;

      // Based off of the application type, we patch the application.
      let lInvalidApp = false;
      const appType = pApp.type;
      if ([ApplicationTypes.MachineLogic, ApplicationTypes.MachineApp].includes(appType)) {
        pApp.sequences.forEach(function(pSequence) {
          pSequence.instructions.forEach(function(pInstruction) {
            if (!lInstructionSet[pInstruction.category] || !lInstructionSet[pInstruction.category][pInstruction.name]) {
              console.error('Encountered invalid instruction wile uploading.', pInstruction);
              lInvalidApp = true;
              return;
            }

            const lInstructionSchema = lInstructionSet[pInstruction.category][pInstruction.name];
            lInstructionSchema.parameters.forEach(function(pParam) {
              lResolveUnitForSingleParam(pInstruction, pParam);
            });
          });
        });
      }

      if (lInvalidApp) { return null; }

      const lNewApp = lThis.addApplication(pApp);
      lNewApp.setVersionHash();
      return lNewApp;
    }).filter(pApp => pApp !== null);

    if(pUploadedApps.length === 0) { pOnComplete(false); }

    const lAppsToUploadPromises = pUploadedApps.map(pApp => {
      return new Promise((resolve) => {
        if(environment.isOnCad()) {
          // NOTE: we need to save to the graph,
          // ilib_ml_core.Application.setDirty is overwritten in the cad and is doing that
          // we will cancel the debounce and deploy immediately without waiting for completion as we dont have any callbacks
          pApp.setDirty(0);
          resolve();
        }
        else {
          pApp.deploy(() => { resolve(); });
        }
      });
    });

    Promise.allSettled(lAppsToUploadPromises)
      .then(() => {
        lThis.mValidator.runValidation();
        pOnComplete(pUploadedApps.at(-1));
      });
  });
};

Library.prototype.removeApplication = function(pApplicationId) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const lApplication = this.getApplicationById(pApplicationId);

  if (!lApplication) {
    console.warn(`Can't remove application [${pApplicationId}] as it does not exist.`);
    return false;
  }


  if (lApplication.isRunning()) {
    console.warn(`Can't remove application [${pApplicationId}] as it is currently active.`);
    return false;
  }

  const lApplicationName = lApplication.getName();

  this.mConfigManager.removeApplicationInCalibrationParts(lApplication);

  const onComplete = () => {
    const lApplicationIndex = this.mApplications.indexOf(lApplication);
    this.mApplications.splice(lApplicationIndex, 1);
    this.notify('onApplicationRemoved', lApplication);
    this.mValidator.clearCacheForApp(lApplicationName);

    const lNextApplication = lApplication.getType() === ApplicationTypes.MachineLogic ? this.getMachineLogicAppList()[0] : this.getPythonAppList()[0];
    this.selectApplication(lNextApplication);
  };

  // If MachineMotion has the app, remove it as well
  machinemotion.library((pLibrary) => {
    if (pLibrary && pLibrary.includes(lApplicationName)) {
      machinemotion.remove(lApplication, (pData) => {
        if (pData) {
          onComplete();
          this.getRobotAppsService().send('MachineLogic/Library.prototype.removeApplication', { applicationId: lApplication.mId });
        }
      });
    } else {
      onComplete();
    }
  }, function(pError){console.error(pError);});

  return true;
};

Library.prototype.moveApplication = function(pFrom, pTo) {
  if(pFrom >= 0 && pFrom < this.mApplications.length && pTo >= 0 && pTo < this.mApplications.length) {
    this.mApplications.splice(pTo, 0, this.mApplications.splice(pFrom, 1)[0]);
    this.notify('onApplicationMoved', pFrom, pTo);
  }
};

Library.prototype.selectApplicationByName = function(pAppName, pForceRefresh = false) {
  // NOTE: ideally we would not allow readOnly apps to be selected, but to keep the UI in sync we will let it, as long as the UI prenvents any changes it shoud be ok
  const lSelectedApp = this.getApplication(pAppName);
  this.selectApplication(lSelectedApp, pForceRefresh);
};

Library.prototype.selectApplication = function(pSelectedApp, pForceRefresh = false) {
  // Prevent changing current application if it is active
  if(!this.mCurrentApplication || !this.mCurrentApplication.isActive()) {
    if(this.mCurrentApplication != pSelectedApp || pForceRefresh) {
      if (this.mCurrentApplication?.getType() === ApplicationTypes.MachineLogic) {
        const initialSequenceName = this.mCurrentApplication.initialSequence.name;
        this.mCurrentApplication.selectSequenceByName(initialSequenceName);
      }

      this.mCurrentApplication = pSelectedApp;
      this.notify('onApplicationSelected', this.mCurrentApplication);

      // Read-Only App can't select sequence, just make sure the UI part is not out of sync.
      if(this.mCurrentApplication && this.mCurrentApplication.isReadOnly()) { this.notify('onSequenceSelected', null); }
    }
  }
};

/**
 * Adds a temporary application
 * @param {Application} pApp
 * @returns {Application}
 */
Library.prototype.addTemporaryApplication = function(pApp) {
  const lExistingApp = this.getApplication(pApp.getName());

  // An app of that name already exists
  if(lExistingApp) {
    // Rename the incoming one
    const _lNewName = this.uniqueApplicationName(pApp.getName());
    console.warn('Application [' + pApp.getName() + '] already exists, renaming to [' + _lNewName + ']');
    pApp.setName(_lNewName);
  }

  this.mNonSavedApplications.push(pApp);
  return pApp;
};

Library.prototype.removeTemporaryApplication = function(pAppName) {
  const lApp = this.mNonSavedApplications.find(pApp => pApp.getName() === pAppName);
  const lIndex = this.mNonSavedApplications.indexOf(lApp);

  if(lApp) {
    if(!lApp.isRunning()) {
      if(lApp == this.mCurrentApplication) {
        this.selectApplicationByName(null);
      }

      this.mNonSavedApplications.splice(lIndex, 1);
      this.notify('onApplicationRemoved', lApp);
      this.mValidator.clearCacheForApp(pAppName);
      return true;
    } else {
      console.warn(`Can't remove application [${pAppName}] as it is currently active.`);
      return false;
    }
  } else {
    console.warn(`Can't remove application [${pAppName}] as it does not exist.`);
    return false;
  }
};

Library.prototype.getCurrentApplication = function() { return this.mCurrentApplication; };

Object.defineProperty(
  Library.prototype,
  'currentApplication',
  {
    get: function () {
      return this.getCurrentApplication();
    }
  }
);

Library.prototype.canSelectApplication = function(pApplication) {
  const lCurrentApp = this.getCurrentApplication();
  if (!lCurrentApp) {
    return true;
  }

  const lIsThisAppActive = lCurrentApp.getName() === pApplication.getName();
  return !lCurrentApp.isActive() || lIsThisAppActive;
};

Library.prototype.getCurrentApplicationIndex = function() {
  if (this.mCurrentApplication == null) {
    return -1;
  }

  const _lCurrentAppName = this.mCurrentApplication.getName();
  return this.getAllApplications().findIndex(function(pApp) {
    return pApp.getName() === _lCurrentAppName;
  });
};

Library.prototype.getRobotAppsService = function() {
  return this.robotAppsService;
};

Library.prototype.toJson = function() {
  const _lApps = [this.mConfigManager.toJson()];
  this.mApplications.forEach(function(pApp) { _lApps.push(pApp.toJson()); });

  return {
    name : 'Library',
    applications : _lApps
  };
};

Library.prototype.reset = function() {
  this.mIsInitialized = false;
  this.mInstructionSet = {};
  this.mApplications = [];
  this.mNonSavedApplications = [];
  if (this.mCurrentApplication) {
    this.mCurrentApplication.reset();
    this.mCurrentApplication = null;
  }
  this.mConfigManager.reset();
  this.notify('onReset');
};

/**
 * @typedef {import('../MachineLogicServices').MachineLogicServices} MachineLogicServices
 */

/**
 * @param {function} pCompletion
 * @param {MachineLogicServices} machineLogicServices
 *
 * @returns {void}
 */
Library.prototype.initialize = function(pCompletion, machineLogicServices) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lThis = this;

  this.reset();

  // Fetch MachineMotion instruction set
  machinemotion.instructionSet(function(pData) {
    if(pData) {
      _lThis.mInstructionSet = pData.instructionSet;
      _lThis.notify('onInstructionSetChanged', _lThis.mInstructionSet);
      _lThis.mConfigManager.loadFromLibrary(function() {

        // Fetch MachineMotion apps
        const _lSeq = new Seq();
        machinemotion.library(function(pData) {
          if(pData) {
            pData.forEach(function(pAppName) {
              if(pAppName != '#global') {
                _lSeq.push(function() {
                  machinemotion.application(pAppName, function(pAppData) {
                    _lThis.addApplication(pAppData);
                    _lSeq.next();
                  });
                });
              }
            });

            // Fetch already executing application
            _lSeq.push(function() {
              machinemotion.status(function(pState) {
                if (!pState) {
                  _lSeq.next();
                  return;
                }

                if (pState.state === ApplicationState.COMPLETED || pState.state === ApplicationState.INACTIVE) {
                  _lSeq.next();
                  return;
                }

                const lActiveApplication = _lThis.getAllApplications()
                  .find(application => application.getName() === pState.application);

                if (lActiveApplication) {
                  _lThis.selectApplication(lActiveApplication);
                  if(lActiveApplication.getType() !== ApplicationTypes.Python) {
                    lActiveApplication.selectSequenceByName(lActiveApplication.listSequences()[0]); // Selecting the main sequence
                  }
                  _lThis.mCurrentApplication.setState(pState);
                }

                _lSeq.next();
              });
            });

            _lSeq.push(function() {
              _lThis.mIsInitialized = true;
              _lThis.notify('onInitialized');
              _lSeq.next();
            });
            if (pCompletion) {
              _lSeq.push(function() {
                pCompletion();
                _lSeq.next();
              });
            }
            _lSeq.start();
          }
        }/*, function(pError){}*/);
      }, machineLogicServices);
    }
  });
};
